// Spotify URL utilities
import { logger } from '@/lib/logger'

export interface SpotifyUrlInfo {
  type: 'track' | 'playlist' | 'album' | 'artist'
  id: string
  url: string
}

/**
 * Parse a Spotify URL and extract type, ID, and clean URL
 * @param url - Spotify URL to parse
 * @returns SpotifyUrlInfo object or null if invalid
 */
export const parseSpotifyUrl = (url: string): SpotifyUrlInfo | null => {
  try {
    // Match Spotify URLs like https://open.spotify.com/track/0Z3Vt4qyrNukxFRhQvhrWG
    const regex = /^https:\/\/open\.spotify\.com\/(track|playlist|album|artist)\/([a-zA-Z0-9]+)(\?.*)?$/
    const match = url.match(regex)
    
    if (!match) {
      return null
    }

    const [, type, id] = match
    return {
      type: type as 'track' | 'playlist' | 'album' | 'artist',
      id,
      url: url.split('?')[0] // Remove query parameters
    }
  } catch (error) {
    logger.error('Error parsing Spotify URL:', error)
    return null
  }
}

/**
 * Validate if a URL is a valid Spotify URL
 * @param url - URL to validate
 * @returns boolean indicating if URL is valid
 */
export const isValidSpotifyUrl = (url: string): boolean => {
  return parseSpotifyUrl(url) !== null
}

/**
 * Get a display name for a Spotify URL type
 * @param type - Spotify content type
 * @returns Capitalized type name
 */
export const getSpotifyTypeDisplayName = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

/**
 * Create a placeholder name for a Spotify item
 * @param urlInfo - Parsed Spotify URL info
 * @returns Placeholder display name
 */
export const createSpotifyPlaceholderName = (urlInfo: SpotifyUrlInfo): string => {
  const displayType = getSpotifyTypeDisplayName(urlInfo.type)
  return `${displayType} (${urlInfo.id.substring(0, 8)}...)`
}

/**
 * Extract Spotify ID from various formats
 * @param input - Can be full URL or just the ID
 * @returns Spotify ID or null if invalid
 */
export const extractSpotifyId = (input: string): string | null => {
  // If it's already just an ID (alphanumeric string)
  if (/^[a-zA-Z0-9]+$/.test(input)) {
    return input
  }
  
  // Try to parse as URL
  const urlInfo = parseSpotifyUrl(input)
  return urlInfo?.id || null
}

/**
 * Build a Spotify URL from type and ID
 * @param type - Spotify content type
 * @param id - Spotify content ID
 * @returns Complete Spotify URL
 */
export const buildSpotifyUrl = (type: string, id: string): string => {
  return `https://open.spotify.com/${type}/${id}`
}

/**
 * Check if a string looks like it could be a Spotify URL
 * @param text - Text to check
 * @returns boolean indicating if it looks like a Spotify URL
 */
export const looksLikeSpotifyUrl = (text: string): boolean => {
  return text.includes('open.spotify.com') || text.includes('spotify:')
}